import React, { useCallback } from 'react';

type Listener = (fullscreen: boolean) => void;

export class ClientFullscreenManager {
  private static instance: ClientFullscreenManager;
  private fullscreen: boolean = false;
  private listeners: Listener[] = [];

  private constructor() {}

  public setFullscreen(value: boolean): void {
    if (value === this.fullscreen) {
      return;
    }

    this.fullscreen = value;
    this.notify();
  }

  public subscribe(listener: Listener): void {
    this.listeners.push(listener);
    listener(this.fullscreen);
  }

  public unsubscribe(listener: Listener): void {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

  private notify(): void {
    this.listeners.forEach(listener => listener(this.fullscreen));
  }

  public static getInstance(): ClientFullscreenManager {
    if (!ClientFullscreenManager.instance) {
      ClientFullscreenManager.instance = new ClientFullscreenManager();
    }
    return ClientFullscreenManager.instance;
  }

  public static reset(): void {
    ClientFullscreenManager.instance = new ClientFullscreenManager();
  }
}

type ReturnType = {
  fullscreen: boolean;
  setFullscreen: (fullscreen: boolean) => void;
};

export function useFullscreen(fullscreen?: boolean): ReturnType {
  const [fullscreenState, setFullscreenState] = React.useState(false);

  const listener = (fullscreen: boolean) => {
    setFullscreenState(fullscreen);
  };

  React.useEffect(() => {
    ClientFullscreenManager.getInstance().subscribe(listener);

    return () => {
      ClientFullscreenManager.getInstance().unsubscribe(listener);
    };
  }, []);

  const setFullscreen = useCallback((fullscreen: boolean) => {
    ClientFullscreenManager.getInstance().setFullscreen(fullscreen);
  }, []);

  React.useEffect(() => {
    if (fullscreen !== undefined) {
      ClientFullscreenManager.getInstance().setFullscreen(fullscreen);
    }
  }, [fullscreen]);

  return {
    fullscreen: fullscreenState,
    setFullscreen,
  };
}
